import { useCallback } from 'react'
import type { Dispatch } from 'react'
import {
  activateCustomer,
  CustomerAuthApiError,
  resendActivationByEmail,
} from '../../../customerAuthApi'
import type { ActivationFlowAction } from './ActivationFlow.types'

type ActivationSubmitMessages = {
  recoveryRateLimit: string
  recoveryError: string
  setupSubmitError: string
}

type UseActivationSubmitHandlersInput = {
  dispatch: Dispatch<ActivationFlowAction>
  token: string
  password: string
  activationFieldsValid: boolean
  normalizedRecoveryEmail: string
  recoveryEmailValid: boolean
  messages: ActivationSubmitMessages
}

type UseActivationSubmitHandlersOutput = {
  handleSetupSubmit: () => Promise<void>
  handleResendActivation: () => Promise<void>
}

export const useActivationSubmitHandlers = ({
  dispatch,
  token,
  password,
  activationFieldsValid,
  normalizedRecoveryEmail,
  recoveryEmailValid,
  messages,
}: UseActivationSubmitHandlersInput): UseActivationSubmitHandlersOutput => {
  const handleResendActivation = useCallback(async () => {
    dispatch({ type: 'RECOVERY_SUBMIT_ATTEMPTED' })

    if (!normalizedRecoveryEmail || !recoveryEmailValid) return

    dispatch({ type: 'RECOVERY_REQUEST_STARTED' })
    try {
      await resendActivationByEmail(normalizedRecoveryEmail)
      dispatch({ type: 'RECOVERY_SUCCESS' })
      return
    } catch (error) {
      if (error instanceof CustomerAuthApiError && error.status === 429) {
        dispatch({
          type: 'RECOVERY_RATE_LIMIT',
          message: messages.recoveryRateLimit,
        })
        return
      }

      dispatch({
        type: 'RECOVERY_FAILURE',
        message: messages.recoveryError,
      })
    }
  }, [
    dispatch,
    messages.recoveryError,
    messages.recoveryRateLimit,
    normalizedRecoveryEmail,
    recoveryEmailValid,
  ])

  const handleSetupSubmit = useCallback(async () => {
    dispatch({ type: 'SETUP_SUBMIT_ATTEMPTED' })

    if (!activationFieldsValid) return

    dispatch({ type: 'SETUP_REQUEST_STARTED' })
    try {
      await activateCustomer(token, password)
      dispatch({ type: 'SETUP_SUCCESS' })
    } catch (error) {
      if (error instanceof CustomerAuthApiError) {
        if (error.status === 410) {
          dispatch({ type: 'SETUP_TOKEN_EXPIRED' })
          return
        }
        if (error.status === 400) {
          dispatch({ type: 'SETUP_TOKEN_INVALID' })
          return
        }
        if (error.message.trim()) {
          dispatch({ type: 'SETUP_FAILURE', message: error.message.trim() })
          return
        }
      }

      dispatch({ type: 'SETUP_FAILURE', message: messages.setupSubmitError })
    }
  }, [activationFieldsValid, dispatch, messages.setupSubmitError, password, token])

  return {
    handleSetupSubmit,
    handleResendActivation,
  }
}
